export const PATTERNS = [
  { id: "grid", label: "Grid" },
  { id: "brick", label: "Brick bond" },
  { id: "stack_vertical", label: "Vertical stack" },
  { id: "herringbone", label: "Herringbone" },
];

export const GROUTS = ["#f4f1ea", "#bdb8ae", "#8a857c", "#4a4743", "#1c1b1a", "#c9a96e"];

const RECT_LABELS = ["Back wall", "Right wall", "Front wall", "Left wall"];
const L_LABELS = ["Back wall", "Right wall", "Notch front", "Notch side", "Front wall", "Left wall"];

export const polygon = (room) => {
  const { length: L, width: W } = room;
  if (room.shape === "L") {
    const cl = Math.min(room.cut_length, L - 0.5);
    const cw = Math.min(room.cut_width, W - 0.5);
    return [[0, 0], [L, 0], [L, W - cw], [L - cl, W - cw], [L - cl, W], [0, W]];
  }
  return [[0, 0], [L, 0], [L, W], [0, W]];
};

export const walls = (room) => {
  const pts = polygon(room);
  const labels = room.shape === "L" ? L_LABELS : RECT_LABELS;
  return pts.map((a, i) => {
    const b = pts[(i + 1) % pts.length];
    const dx = b[0] - a[0];
    const dz = b[1] - a[1];
    return { key: `w${i}`, label: labels[i], a, b, len: Math.hypot(dx, dz), nx: -dz, nz: dx };
  });
};

export const floorArea = (room) => {
  const p = polygon(room);
  let s = 0;
  p.forEach((a, i) => { const b = p[(i + 1) % p.length]; s += a[0] * b[1] - b[0] * a[1]; });
  return Math.abs(s) / 2;
};

const surf = (tile_id, extra = {}) => ({ tile_id, pattern: "grid", tile_w: 600, tile_h: 600, grout_mm: 3, grout_colour: "#bdb8ae", tiled_height: null, ...extra });

export const defaultState = () => ({
  room: { shape: "rect", length: 3.0, width: 2.4, height: 2.4, cut_length: 1.2, cut_width: 1.0 },
  paint: "#ece6dc",
  brassware: "tap_black",
  surfaces: {
    floor: surf("travertino-romano"),
    w0: surf("calacatta-bianco", { tile_w: 600, tile_h: 1200 }),
    w1: surf("calacatta-bianco", { tile_w: 600, tile_h: 1200, tiled_height: 1.2 }),
    w2: surf(null),
    w3: surf("calacatta-bianco", { tile_w: 600, tile_h: 1200, tiled_height: 1.2 }),
    w4: surf(null),
    w5: surf(null),
  },
  fixtures: [
    { uid: "f1", type: "bath", x: 0.85, z: 0.375, rot: 0 },
    { uid: "f2", type: "vanity", x: 2.75, z: 1.2, rot: -90 },
    { uid: "f3", type: "toilet", x: 2.0, z: 0.275, rot: 0 },
  ],
});

const uid = () => Math.random().toString(36).slice(2, 8);
const num = (v) => (typeof v === "number" && isFinite(v) ? v : undefined);
const pick = (o, keys) => Object.fromEntries(keys.filter((k) => o[k] !== undefined).map((k) => [k, o[k]]));
const SURF_KEYS = ["tile_id", "pattern", "tile_w", "tile_h", "grout_mm", "grout_colour", "tiled_height"];

export const clampFixture = (f, room, meta) => {
  const w = meta?.w || 0.4, d = meta?.d || 0.4, r = (f.rot * Math.PI) / 180;
  const hx = (Math.abs(Math.cos(r)) * w + Math.abs(Math.sin(r)) * d) / 2;
  const hz = (Math.abs(Math.sin(r)) * w + Math.abs(Math.cos(r)) * d) / 2;
  let x = Math.max(hx, Math.min(room.length - hx, f.x));
  let z = Math.max(hz, Math.min(room.width - hz, f.z));
  if (room.shape === "L") {
    const nx = room.length - Math.min(room.cut_length, room.length - 0.5);
    const nz = room.width - Math.min(room.cut_width, room.width - 0.5);
    const dx = x + hx - nx, dz = z + hz - nz;
    if (dx > 0 && dz > 0) { if (dx < dz) x = nx - hx; else z = nz - hz; }
  }
  return { ...f, x, z };
};

const BASIN_MODELS = ["vanity", "basin_counter"];
export const basinCount = (state, meta) => state.fixtures.reduce((n, f) => { const m = meta[f.type]; return m && BASIN_MODELS.includes(m.model) ? n + (m.double ? 2 : 1) : n; }, 0);

export function applyActions(state, actions, fixtureMeta = {}, collections = []) {
  let s = JSON.parse(JSON.stringify(state));
  const tileIds = new Set(collections.map((c) => c.id));
  actions.forEach((a) => {
    if (a.op === "set_room") {
      const r = pick(a, ["shape", "length", "width", "height", "cut_length", "cut_width"]);
      ["length", "width", "height", "cut_length", "cut_width"].forEach((k) => { if (r[k] !== undefined) r[k] = Math.max(0.5, Math.min(12, Number(r[k]) || s.room[k])); });
      s.room = { ...s.room, ...r };
    } else if (a.op === "set_surface") {
      const patch = pick(a, SURF_KEYS);
      if (patch.tile_id && !tileIds.has(patch.tile_id)) delete patch.tile_id;
      const c = collections.find((x) => x.id === patch.tile_id);
      if (c && a.tile_w === undefined) { patch.tile_w = c.tile_w; patch.tile_h = c.tile_h; }
      const targets = a.target === "all_walls" ? walls(s.room).map((w) => w.key) : [a.target];
      targets.forEach((t) => { if (s.surfaces[t] || /^w\d$/.test(t) || t === "floor") s.surfaces[t] = { ...(s.surfaces[t] || surf(null)), ...patch }; });
    } else if (a.op === "set_paint" && a.colour) {
      s.paint = a.colour;
    } else if (a.op === "set_brassware" && a.item) {
      s.brassware = a.item;
    } else if (a.op === "add_fixture" && fixtureMeta[a.type]) {
      const m = fixtureMeta[a.type];
      s.fixtures.push({ uid: uid(), type: a.type, x: num(a.x) ?? s.room.length / 2, z: num(a.z) ?? m.d / 2, rot: num(a.rot) ?? 0 });
    } else if (a.op === "move_fixture") {
      s.fixtures = s.fixtures.map((f) => (f.uid === a.uid || (!a.uid && f.type === a.type) ? { ...f, ...pick({ x: num(a.x), z: num(a.z), rot: num(a.rot) }, ["x", "z", "rot"]) } : f));
    } else if (a.op === "remove_fixture") {
      s.fixtures = s.fixtures.filter((f) => !(f.uid === a.uid || (!a.uid && f.type === a.type)));
    }
  });
  s.fixtures = s.fixtures.map((f) => clampFixture(f, s.room, fixtureMeta[f.type]));
  return s;
}

export function quantities(state, collections, fixtureMeta, brassware = []) {
  const byId = Object.fromEntries(collections.filter((c) => !c.panel).map((c) => [c.id, c]));
  const panelById = Object.fromEntries(collections.filter((c) => c.panel).map((c) => [c.id, c]));
  const areas = {};
  const pcount = {};
  const add = (id, a) => { if (id && byId[id]) areas[id] = (areas[id] || 0) + a; };
  add(state.surfaces.floor?.tile_id, floorArea(state.room));
  walls(state.room).forEach((w) => {
    const sp = state.surfaces[w.key];
    if (panelById[sp?.tile_id]) pcount[sp.tile_id] = (pcount[sp.tile_id] || 0) + 1;
    else if (sp?.tile_id) add(sp.tile_id, w.len * Math.min(sp.tiled_height || state.room.height, state.room.height));
  });
  const panels = Object.entries(pcount).map(([id, n]) => ({ ...panelById[id], qty: n, est: n * (panelById[id].price_panel || 0) }));
  const brass = brassware.find((b) => b.id === state.brassware);
  const nb = basinCount(state, fixtureMeta);
  const tap = brass && nb ? { ...brass, qty: nb, est: nb * brass.price_gbp } : null;
  const tiles = Object.entries(areas).map(([id, a]) => {
    const order = Math.ceil(a * 1.1 * 10) / 10;
    return { ...byId[id], area: a, order, est: order * (byId[id].price_m2 || 0) };
  });
  const fixtures = state.fixtures.map((f) => ({ ...f, meta: fixtureMeta[f.type] })).filter((f) => f.meta);
  const total = tiles.reduce((s, t) => s + t.est, 0) + panels.reduce((s, p) => s + p.est, 0) + (tap?.est || 0) + fixtures.reduce((s, f) => s + (f.meta.price_gbp || 0), 0);
  const tiled = Object.values(areas).reduce((s, a) => s + a, 0);
  return { tiles, panels, tap, fixtures, total, tiled, floor: floorArea(state.room) };
}
